import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";

/**
 * Poster gallery — replaces the intro reel on the landing hero.
 *
 * Shows the two A4 keepsake posters (Birth Chart + Compatibility) side by side.
 * Tap either to open a lightbox for a full-quality look before checkout.
 */
const POSTERS = [
  {
    slug: "birth-chart",
    src: "/sample-birth-chart.png",
    label: "Cosmic Birth Chart",
    price: "Included",
    to: "/birth-chart",
    caption: "Sun · Moon · Rising · essence · affirmation — screenshot & keep.",
  },
  {
    slug: "compat",
    src: "/sample-compat.png",
    label: "Compatibility Reading",
    price: "Included",
    to: "/compatibility",
    caption: "Two natal charts, one harmony score, a shared ritual + your song.",
  },
];

export default function PosterGallery() {
  const [open, setOpen] = useState(null);

  return (
    <div data-testid="poster-gallery" className="w-full">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
        {POSTERS.map((p) => (
          <button
            key={p.slug}
            type="button"
            onClick={() => setOpen(p)}
            data-testid={`poster-thumb-${p.slug}`}
            className="group text-left rounded-2xl border border-[color:var(--gold)]/25 overflow-hidden hover:border-[color:var(--gold)]/60 transition"
            style={{ background: "rgba(10, 17, 40, 0.5)" }}
          >
            <div className="aspect-[3508/2480] overflow-hidden">
              <img
                src={p.src}
                alt={`Sample ${p.label} poster`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="px-4 py-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-serif-display text-[color:var(--parchment)] text-base md:text-lg truncate">
                  {p.label}
                </div>
                <div className="font-serif-italic text-[color:var(--text-secondary)] text-xs truncate">
                  {p.caption}
                </div>
              </div>
              <span className="flex-shrink-0 label-overline text-[color:var(--gold)]">
                {p.price}
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {open && (
        <div
          data-testid="poster-lightbox"
          className="fixed inset-0 z-50 bg-[color:var(--bg-base)]/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setOpen(null)}
        >
          <button
            type="button"
            onClick={() => setOpen(null)}
            aria-label="Close preview"
            data-testid="poster-lightbox-close"
            className="absolute top-4 right-4 w-10 h-10 rounded-full border border-[color:var(--gold)]/40 flex items-center justify-center text-[color:var(--parchment)] hover:bg-[color:var(--gold)]/10"
          >
            <X size={18} />
          </button>
          <div
            className="max-w-6xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={open.src}
              alt={`${open.label} large preview`}
              className="w-full h-auto rounded-xl border border-[color:var(--gold)]/30"
            />
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="label-overline text-[color:var(--gold)]">Sample</div>
                <div className="font-serif-display text-[color:var(--parchment)] text-xl md:text-2xl">
                  {open.label}
                </div>
                <div className="font-serif-italic text-[color:var(--text-secondary)] text-sm mt-1">
                  {open.caption}
                </div>
              </div>
              <Link
                to={open.to}
                data-testid="poster-lightbox-cta"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium"
              >
                <Sparkles size={14} /> Open reading
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
